import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CountryFlag from 'react-country-flag';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import ProfileCustomClearIcon from '../../../HelperIcons/ProfileCustomClearIcon';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

const FlagContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: theme.spacing(1),
  paddingRight: theme.spacing(1),
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const getDisplayValue = (field, option) => {
  let displayValue;
  if (field.id === 'nationality') {
    displayValue = option?.nation;
  } else if (field.id === 'country') {
    displayValue = option?.name;
  } else {
    displayValue = option?.label;
  };
  return displayValue;
}

const CustomPanelForm = ({
  field,
  index,
  handleFieldChange,
  handleClearField,
  shudClearBtnRender,
  labelColor = '#8387BD',
  fieldBorderColor = '#E7E7FF',
  fieldHoverBorderColor = '#8387BD',
  fieldErrorBorderColor = '#f44336',
  borderRadius = '10px'
}) => {
  
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (touched) {
      if (field.required && field.isRequiredAspectInvalid) {
        setError(true);
        setErrorMessage('This field is required');
      } else {
        setError(false);
        setErrorMessage('');
      }
    }
  }, [touched, field.required, field.isRequiredAspectInvalid]);

  const normalizeTimeZone = (value) => {
    if (value === "Asia/Calcutta") return "Asia/Kolkata";
    return value;
  };

  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ marginBottom: '20px', padding: 0 }}>
      <Typography variant="body1" sx={{ width: '150px', color: labelColor, fontFamily: 'Figtree-Regular' }}>
        {field.label}{field.required && <Box component="span" sx={{ color: labelColor }}> *</Box>}
      </Typography>
      {field.type === 'text' ? (
        <TextField
          fullWidth
          margin="normal"
          value={field.value}
          onChange={e => handleFieldChange(index, e, undefined, field.id)}
          onBlur={() => setTouched(true)}
          disabled={field.disabled}
          error={error}
          helperText={error ? errorMessage : ''}
          sx={{
            pointerEvents: field.disabled ? 'none' : 'auto',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: `${error ? fieldErrorBorderColor : fieldBorderColor} !important`,
                borderRadius: borderRadius,
              },
              '&:hover': {
                borderColor: `${error ? fieldErrorBorderColor : fieldHoverBorderColor} !important`,
              },
              '&:hover fieldset': {
                borderColor: `${error ? fieldErrorBorderColor : fieldHoverBorderColor} !important`,
                cursor: 'pointer',
              },
              '&.Mui-focused fieldset': {
                borderColor: `${error ? fieldErrorBorderColor : fieldHoverBorderColor} !important`,
              },
            },
            '& .MuiInputBase-input': {
              fontFamily: 'Figtree-Regular',
              fontSize: "13px"
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {shudClearBtnRender && (
                  <IconButton
                    aria-label="clear"
                    onClick={e => handleClearField(index, e)}
                    disabled={field.disabled}
                    edge="end"
                  >
                    <ProfileCustomClearIcon
                      sx={{
                        backgroundColor: field.disabled ? 'transparent' : 'lightgrey',
                        color: 'white',
                        borderRadius: '50%',
                        width: '18px',
                        height: '18px',
                      }}
                    />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      ) : (
        <FormControl fullWidth sx={{ pointerEvents: field.disabled ? 'none' : 'auto' }}>
          <InputLabel id={`${field.id}-label`} sx={{ fontFamily: 'Figtree-Regular', fontSize: "13px", '&.Mui-focused': { color: `${fieldHoverBorderColor} !important`, }, }}>
            {field.label}{field.required && <Box component="span" sx={{ color: 'red' }}> *</Box>}
          </InputLabel>
          <Select
            labelId={`${field.id}-label`}
            value={field.value?.toLowerCase() === 'select country' ? '' : normalizeTimeZone(field.value)}
            onChange={(e) => handleFieldChange(index, e)}
            disabled={field.disabled}
            label={field.label}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                  maxWidth: 300
                },
              },
            }}
            renderValue={(selected) => {
              const option = field.options.find(opt => opt.code === selected || opt.name === selected || opt.value === selected);
              return (
                <Stack direction="row" alignItems="center">
                  <>
                    {option?.code && <FlagContainer>
                      <CountryFlag countryCode={option?.code} svg />
                    </FlagContainer>}
                    <Typography sx={{ fontSize: "13px" }}>{getDisplayValue(field, option)}</Typography>
                  </>
                </Stack>
              );
            }}
            sx={{
              borderColor: fieldBorderColor,
              borderRadius: borderRadius,
              '& fieldset': {
                borderColor: `${fieldBorderColor} !important`,
                borderRadius: borderRadius,
                "legend": {
                  "span": {
                    color: `${fieldHoverBorderColor} !important`,
                  }
                }
              },
              '&:hover': {
                borderColor: `${fieldHoverBorderColor} !important`,
              },
              '&:hover fieldset': {
                borderColor: `${fieldHoverBorderColor} !important`,
              },
              '&.Mui-focused fieldset': {
                borderColor: `${fieldHoverBorderColor} !important`,
              },
              '& .MuiInputBase-input': {
                borderColor: `${fieldHoverBorderColor} !important`,
                color: '#15194B',
                'WebkitTextFillColor': '#15194B !important',
                fontFamily: 'Figtree-Regular',
              }
            }}
          >
            {field.options && field.options.map(option => (
              <MenuItem key={option?.code || option.value} value={option.code || option.value} sx={{ fontFamily: 'Figtree-Regular' }}>
                <FlagContainer>
                  <CountryFlag countryCode={option?.code} svg />
                </FlagContainer>
                {getDisplayValue(field, option)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Stack>
  );
};

export default CustomPanelForm;
